import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, ArrowLeft, ShoppingCart, Image as ImageIcon, Loader2, Download, CheckSquare, Square, Package } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useCart } from "../contexts/CartContext";
import TheaterMode from "../components/TheaterMode";
import FloatingCart from "../components/FloatingCart";
import BrandingFooter from "../components/BrandingFooter";
import FloatingBrandingBadge from "../components/FloatingBrandingBadge";
import SEO from "../components/SEO";

const PublicGalleryDetail = () => {
  const { galleryId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Theater mode state
  const [theaterOpen, setTheaterOpen] = useState(false);
  const [theaterIndex, setTheaterIndex] = useState(0);
  
  // Use cart context for persistent cart
  const { cart, addToCart: contextAddToCart, clearCart, cartGalleryId, switchGallery } = useCart();
  
  // Pricing state for sellable galleries
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  
  // Download state for paid private galleries
  const [selectMode, setSelectMode] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  // Switch gallery context when galleryId changes
  useEffect(() => {
    if (galleryId) {
      switchGallery(galleryId);
    }
  }, [galleryId]);

  useEffect(() => {
    fetchGallery();
  }, [galleryId]);

  // Fetch pricing when gallery is loaded and it's a sellable public gallery
  useEffect(() => {
    if (gallery && gallery.is_public && !isPaidPrivate) {
      fetchPricing();
    }
  }, [gallery?.gallery_id]);

  // Prevent right-click on images (only for non-paid galleries)
  useEffect(() => {
    const handleContextMenu = (e) => {
      // Allow right-click if it's a paid private gallery
      if (gallery?.is_paid_private || gallery?.can_download) return;
      
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [gallery]);

  const fetchGallery = async () => {
    try {
      // First try to fetch as access token (for delivered private galleries)
      try {
        const response = await axios.get(`${API}/galleries/access/${galleryId}`);
        setGallery(response.data);
        setAssets(response.data.assets || []);
        return;
      } catch (accessError) {
        // If not found via access token, try public gallery
        if (accessError.response?.status !== 404) {
          throw accessError;
        }
      }
      
      // Try as public gallery
      const response = await axios.get(`${API}/galleries/public/${galleryId}`);
      setGallery(response.data);
      setAssets(response.data.assets || []);
    } catch (error) {
      if (error.response?.status === 410) {
        toast.error("This gallery has expired");
      } else {
        toast.error("Gallery not found");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPricing = async () => {
    setPricingLoading(true);
    try {
      const response = await axios.get(`${API}/downloads/public/gallery/${galleryId}`);
      setPricing(response.data);
      
      // Update assets with pricing info
      if (response.data.photos) {
        setAssets(prev => prev.map(asset => {
          const pricedPhoto = response.data.photos.find(p => p.asset_id === asset.asset_id);
          return pricedPhoto ? { ...asset, price: pricedPhoto.price } : asset;
        }));
      }
    } catch (error) {
      // Pricing not available - gallery may not have downloads enabled
      console.log("Pricing not available for this gallery");
    } finally {
      setPricingLoading(false);
    }
  };

  // Get dynamic branding
  const brandName = gallery?.photographer?.company_name || "360Lumina";
  const logoUrl = gallery?.photographer?.logo_url ? `${API.replace("/api", "")}${gallery.photographer.logo_url}` : null;
  
  // Generate page title with fallback order:
  // 1. Photographer branding/business name + gallery title
  // 2. Gallery title only
  // 3. "360Lumina | Professional Photography" (final fallback)
  const pageTitle = useMemo(() => {
    const photographerName = gallery?.photographer?.company_name || gallery?.photographer?.user?.name;
    const galleryTitle = gallery?.title;
    
    if (photographerName && galleryTitle) {
      return `${galleryTitle} | ${photographerName}`;
    }
    if (galleryTitle) {
      return `${galleryTitle} | Professional Photography`;
    }
    if (photographerName) {
      return `${photographerName} | Professional Photography`;
    }
    return "360Lumina | Professional Photography";
  }, [gallery]);
  
  // Check if this is a paid private gallery (no watermarks, can download)
  const isPaidPrivate = gallery?.is_paid_private || (gallery?.is_delivered && !gallery?.is_public);
  const canDownload = gallery?.can_download || isPaidPrivate;
  
  // Download functions
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };
  
  const selectAll = () => {
    if (selectedAssets.size === assets.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(assets.map(a => a.asset_id)));
    }
  };
  
  const downloadSingle = async (assetId) => {
    try {
      const link = document.createElement('a');
      link.href = `${API}/galleries/download/${galleryId}/${assetId}`;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download");
    }
  };
  
  const downloadSelected = async () => {
    if (selectedAssets.size === 0) {
      toast.error("No photos selected");
      return;
    }
    
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/galleries/download/${galleryId}/bulk`,
        { asset_ids: Array.from(selectedAssets) },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${gallery?.title || 'photos'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started");
      setSelectMode(false);
      setSelectedAssets(new Set());
    } catch (error) {
      toast.error("Failed to download photos");
    } finally {
      setDownloading(false);
    }
  };
  
  const downloadAll = async () => {
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/galleries/download/${galleryId}/bulk`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${gallery?.title || 'photos'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download photos");
    } finally {
      setDownloading(false);
    }
  };
  
  // Generate back link - go to photographer galleries or photographer landing page
  const getBackLink = () => {
    const photog = gallery?.photographer;
    if (!photog) return "/galleries";
    
    const businessName = photog.company_name || photog.user?.name;
    if (businessName) {
      return `/galleries?photographer=${gallery.photographer_id}`;
    }
    return "/galleries";
  };
  
  // Generate link back to photographer's main landing page
  const getPhotographerLink = () => {
    const photog = gallery?.photographer;
    if (!photog) return "/";
    
    const businessName = photog.company_name || photog.user?.name;
    if (businessName) {
      const urlSafeName = businessName
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
      return `/photographer/${urlSafeName}`;
    }
    return "/";
  };

  // Cart functions - use context
  const addToCart = (asset) => {
    const added = contextAddToCart(asset, galleryId);
    if (added === false) {
      toast.info("Photo already in cart");
      return;
    }
    toast.success("Added to cart!");
  };

  // Theater mode
  const openTheaterMode = (index) => {
    setTheaterIndex(index);
    setTheaterOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-2xl mb-4">Gallery Not Found</h1>
          <Link to="/galleries">
            <Button>Browse Galleries</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen py-12 px-6" 
      data-testid="public-gallery-detail"
      onContextMenu={(e) => {
        if (e.target.tagName === 'IMG') {
          e.preventDefault();
        }
      }}
    >
      {/* Dynamic SEO - ensures browser title is always valid */}
      <SEO 
        title={pageTitle}
        description={gallery?.description || `View ${gallery?.title || 'this gallery'} - Professional photography by ${brandName}`}
        noIndex={isPaidPrivate} // Don't index private delivered galleries
      />
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-12">
        <div className="flex items-center justify-between mb-8">
          {/* Show different navigation for public vs private galleries */}
          {!isPaidPrivate ? (
            <Link to={getBackLink()} className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Galleries
            </Link>
          ) : (
            <Link to={getPhotographerLink()} className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {gallery?.photographer?.company_name || gallery?.photographer?.user?.name || "Back"}
            </Link>
          )}
          
          <div className="flex items-center gap-2">
            {/* Download buttons for paid private galleries */}
            {canDownload && (
              <>
                {selectMode ? (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={selectAll}
                    >
                      {selectedAssets.size === assets.length ? (
                        <><CheckSquare className="w-4 h-4 mr-2" /> Deselect All</>
                      ) : (
                        <><Square className="w-4 h-4 mr-2" /> Select All</>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setSelectMode(false); setSelectedAssets(new Set()); }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={downloadSelected}
                      disabled={downloading || selectedAssets.size === 0}
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download ({selectedAssets.size})
                    </Button>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectMode(true)}
                    >
                      <CheckSquare className="w-4 h-4 mr-2" />
                      Select Photos
                    </Button>
                    <Button 
                      size="sm"
                      onClick={downloadAll}
                      disabled={downloading}
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Download All
                    </Button>
                  </>
                )}
              </>
            )}
            
            {/* Cart Button - only show for public/for-sale galleries */}
            {!canDownload && pricing?.downloads_enabled && (
              <Badge variant="secondary" className="text-sm">
                <Package className="w-3 h-3 mr-1" />
                Photos for sale
              </Badge>
            )}
          </div>
        </div>
        
        <Link to={getPhotographerLink()} className="flex items-center gap-3 mb-6 hover:opacity-80 transition-opacity">
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-8 max-w-[140px] object-contain" />
          ) : (
            <>
              <Camera className="w-8 h-8 text-primary" />
              <span className="text-2xl font-serif">{brandName}</span>
            </>
          )}
        </Link>
        
        <h1 className="text-4xl md:text-5xl tracking-tight mb-4">{gallery.title}</h1>
        <p className="text-muted-foreground text-lg">
          {canDownload 
            ? `${assets.length} photos ready for download` 
            : pricing?.downloads_enabled
              ? `${assets.length} photos available for purchase`
              : `${assets.length} photos`
          }
        </p>
        {/* Package pricing info */}
        {pricing?.downloads_enabled && pricing?.package_price && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-lg">
            <Package className="w-4 h-4 text-primary" />
            <span className="text-sm">
              Buy all {assets.length} photos for <span className="font-serif font-medium">${pricing.package_price.toFixed(2)}</span>
            </span>
          </div>
        )}
        {gallery.expires_at && canDownload && (
          <p className="text-yellow-500 text-sm mt-1">
            Access expires: {new Date(gallery.expires_at).toLocaleDateString()}
          </p>
        )}
        {gallery.description && (
          <p className="text-muted-foreground mt-2">{gallery.description}</p>
        )}
      </div>

      <div className="max-w-7xl mx-auto">
        {assets.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-lg">No photos in this gallery yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {assets.map((asset, index) => {
              const inCart = cart.find(item => item.asset_id === asset.asset_id);
              const isSelected = selectedAssets.has(asset.asset_id);
              
              return (
                <motion.div
                  key={asset.asset_id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="relative group cursor-pointer"
                  onClick={() => selectMode ? toggleAssetSelection(asset.asset_id) : openTheaterMode(index)}
                >
                  <div className={`aspect-square bg-muted rounded-sm overflow-hidden ${
                    isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 
                    isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 
                    inCart ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                  }`}>
                    <img
                      src={asset.thumbnail_path 
                        ? `${API.replace('/api', '')}${asset.thumbnail_path}`
                        : `${API}/media/${galleryId}/${asset.filename}`
                      }
                      alt={asset.filename}
                      loading="lazy"
                      className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 select-none ${
                        selectMode && !isSelected ? 'opacity-70' : ''
                      }`}
                      draggable={canDownload}
                      onContextMenu={(e) => !canDownload && e.preventDefault()}
                    />
                    
                    {/* Watermark overlay - only for unpaid galleries */}
                    {!isPaidPrivate && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="text-white/20 text-4xl font-serif rotate-[-30deg] select-none">
                          PREVIEW
                        </span>
                      </div>
                    )}
                    
                    {/* Selection checkbox for paid private galleries */}
                    {canDownload && selectMode && (
                      <div className="absolute top-2 left-2 z-10">
                        {isSelected ? (
                          <CheckSquare className="w-6 h-6 text-primary" />
                        ) : (
                          <Square className="w-6 h-6 text-white/70" />
                        )}
                      </div>
                    )}
                    
                    {/* In Cart Badge - only for purchase galleries */}
                    {!canDownload && inCart && (
                      <div className="absolute top-2 right-2 z-10">
                        <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded flex items-center gap-1">
                          <ShoppingCart className="w-3 h-3" />
                          In Cart
                        </span>
                      </div>
                    )}
                    
                    {/* Price badge - only for purchase galleries */}
                    {!canDownload && pricing?.downloads_enabled && asset.price && (
                      <div className="absolute bottom-2 right-2 px-3 py-1 bg-primary rounded-sm text-sm font-medium">
                        ${asset.price.toFixed(2)}
                      </div>
                    )}
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="text-center">
                        {canDownload ? (
                          <>
                            <span className="text-sm uppercase tracking-widest mb-2 block">
                              {selectMode ? 'Click to select' : 'Click to view'}
                            </span>
                            {!selectMode && (
                              <Button
                                size="sm"
                                className="mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadSingle(asset.asset_id);
                                }}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="text-sm uppercase tracking-widest mb-2 block">
                              Click to view
                            </span>
                            {pricing?.downloads_enabled && asset.price && !inCart && (
                              <Button
                                size="sm"
                                className="mt-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart(asset);
                                }}
                              >
                                <ShoppingCart className="w-4 h-4 mr-1" />
                                Add ${asset.price.toFixed(2)}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Theater Mode */}
      <TheaterMode
        isOpen={theaterOpen}
        onClose={() => setTheaterOpen(false)}
        assets={assets}
        currentIndex={theaterIndex}
        onIndexChange={setTheaterIndex}
        galleryId={galleryId}
        apiUrl={API}
        isPublic={true}
        onAddToCart={pricing?.downloads_enabled ? addToCart : undefined}
        disableRightClick={true}
      />

      {/* Floating Cart - only for galleries with downloads enabled */}
      {!canDownload && pricing?.downloads_enabled && (
        <FloatingCart
          galleryId={galleryId}
          galleryTitle={gallery.title}
          packagePrice={pricing?.package_price}
          allowPackage={pricing?.package_price != null}
          photoCount={assets.length}
          apiBaseUrl={API}
        />
      )}

      {/* Branding Footer */}
      <footer className="border-t border-border mt-12 py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          <BrandingFooter planKey={gallery?.photographer_plan_key} />
        </div>
      </footer>

      {/* Floating branding badge for Starter/Pro tiers */}
      <FloatingBrandingBadge planKey={gallery?.photographer_plan_key} />
    </div>
  );
};

export default PublicGalleryDetail;
